/**
 * Two short synthesised tones for menu chrome — a hover tick and a confirm
 * blip — for the screens outside the arena that have never had any sound at
 * all. `game/audio/` is the battle's own engine: a shared voice cap, ducking,
 * recorded cues alongside synthesised ones — built for a firefight with up to
 * a dozen tanks on screen. A modal has three interactive elements and no
 * concurrency to manage, so reaching for that engine here would mean either
 * dragging arena machinery into the garage or building a Babylon `Scene` this
 * screen has no other reason to have. This is the garage's own, and it is
 * deliberately small: two `OscillatorNode`s and a volume envelope, nothing
 * this file doesn't need.
 *
 * Synthesised for the same reason the battle's cues are: nothing to fetch,
 * nothing to fail to decode, and the game stays silent-but-working on a
 * connection that can't load an asset.
 *
 * The `AudioContext` is created lazily, on the first call, and only once —
 * never at module load, which on some browsers is itself enough to trigger an
 * autoplay warning before anything has actually asked to make sound. Browsers
 * also start a context `suspended` until a user gesture; `resume()` is cheap
 * to call every time and a no-op once it has already taken.
 */

let ctx: AudioContext | null = null;

function context(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  if (!ctx) ctx = new Ctor();
  if (ctx.state === 'suspended') void ctx.resume();
  return ctx;
}

/** Forgets the shared context. Only for tearing one down in a test — same
 *  reason `game/audio/synth.ts`'s `resetSharedNoise` exists: without it, a
 *  suite that stubs a fresh fake `AudioContext` per test would still find
 *  this module holding onto the previous test's instance. */
export function resetUiAudioContext(): void {
  ctx = null;
}

/**
 * One tone, ramped up fast and decayed out — a click without a sharp edge,
 * which is what a linear-attack blip sounds like on a menu.
 */
function blip(freq: number, durationSec: number, peakGain: number): void {
  const audio = context();
  if (!audio) return;
  const osc = audio.createOscillator();
  const gain = audio.createGain();
  osc.type = 'triangle';
  osc.frequency.value = freq;
  const t0 = audio.currentTime;
  gain.gain.setValueAtTime(0.0001, t0);
  gain.gain.exponentialRampToValueAtTime(peakGain, t0 + 0.006);
  gain.gain.exponentialRampToValueAtTime(0.0001, t0 + durationSec);
  osc.connect(gain).connect(audio.destination);
  osc.start(t0);
  osc.stop(t0 + durationSec + 0.02);
}

/** A light tick for hovering an interactive row or button. Quiet on purpose —
 *  this one plays every time a pointer crosses a boundary, not once per
 *  decision, so it has to be the quietest sound in the room. */
export function playUiHover(): void {
  blip(660, 0.05, 0.035);
}

/** A brighter, two-partial blip for a click that actually did something —
 *  closing the window, claiming a reward. The second, higher tone rides in
 *  40ms behind the first, which is what makes a "confirm" read as separate
 *  from the plainer hover tick rather than as the same sound turned up. */
export function playUiConfirm(): void {
  blip(520, 0.075, 0.07);
  const audio = context();
  if (!audio) return;
  window.setTimeout(() => blip(780, 0.09, 0.06), 40);
}
